import { Box, Button, Grid, useMediaQuery, Link, type Theme } from '@mui/material';
import { HiredBy } from '@care/caregiver-profile-components';
import { Divider } from '@care/react-component-lib';
import { AmplitudeEvent, trackAmplitudeEvent } from '@/lib/analyticsHelper';
import {
  getSearchResults_seoSegmentationSearchResults_SEOSegmentationSearchResultsSuccess_caregiverSearchResults as SEOSegmentationCaregiverSearchResult,
  getSearchResults_seoSegmentationSearchResults_SEOSegmentationSearchResultsSuccess_caregiverSearchResults_caregiver as Caregiver,
} from '@/__generated__/getSearchResults';
import AssistedBioIndicator from '@/components/AssistedBioIndicator';
import ProfileReview from '../ProfileReview';
import RobustCaregiverInfo from './RobustCaregiverInfo';
import CaregiverProfileAvatar from './CaregiverProfileAvatar';
import CaregiverPayRange from './CaregiverPayRange';
import CaregiverBioTitle from './CaregiverBioTitle';
import CaregiverRating from './CaregiverRating';
import CaregiverServices from './CaregiverServices';
import CaregiverSkills from './CaregiverSkills';
import CaregiverBio from './CaregiverBio';

interface RobustCaregiverCardProps {
  caregiver: Caregiver;
  caregiverReviews: SEOSegmentationCaregiverSearchResult['caregiverReviews'];
  isClientSearchResultsQuery: boolean;
  url: SEOSegmentationCaregiverSearchResult['profileURL'];
}
// eslint-disable-next-line complexity
const RobustCaregiverCard = ({
  caregiver,
  caregiverReviews,
  isClientSearchResultsQuery,
  url,
}: RobustCaregiverCardProps) => {
  const isMobileOrSmaller = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const isMediumOrLarger = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));
  const { hiredTimes } = caregiver;
  const caregiverProfile = caregiver.profiles?.commonCaregiverProfile;
  const targetValue = isMobileOrSmaller ? '' : '_blank';
  const firstName = caregiver.member?.displayName?.split(' ')[0];
  const ctaText = `See ${firstName}'s profile`;
  const isAiAssistedBio = caregiverProfile?.bio?.aiAssistedBio ?? false;

  const handleCtaBtn = (btnText: string) => {
    const amplitudeData = {
      cta_text: btnText,
      cta_type: 'button',
    };

    trackAmplitudeEvent(AmplitudeEvent.seoFeatureInteracted, amplitudeData);
  };
  if (url === null) return null;
  return (
    <Grid item xs={12}>
      <Box mb={{ xs: 2 }} pb={{ xs: 0, sm: 2 }} component="div" data-testid="robust-caregiver-card">
        <Box display="flex" pb={{ xs: 1, md: 0.5 }}>
          <Box pr={{ xs: 1.5, md: 3 }} pt={{ xs: 0.5, md: 0 }}>
            <Link href={url} target={targetValue} data-testid="caregiver-avatar" underline="none">
              <CaregiverProfileAvatar
                caregiver={caregiver}
                isClientSearchResultsQuery={isClientSearchResultsQuery}
              />
            </Link>
          </Box>
          <Box display="flex" flexDirection="row" justifyContent="space-between" width="100%">
            <Box display="flex" flexDirection="column">
              <RobustCaregiverInfo caregiver={caregiver} url={url} />

              <Box display="flex" flexDirection="column" alignItems="flex-start">
                <CaregiverPayRange caregiver={caregiver} />
              </Box>

              {caregiverProfile?.bio.title && isMediumOrLarger && (
                <Box display="flex" flexDirection="column" alignItems="flex-start">
                  <CaregiverBioTitle caregiver={caregiver} />
                </Box>
              )}
            </Box>
            <Box display="flex" flexDirection="column">
              <CaregiverRating caregiver={caregiver} />
              {isMediumOrLarger && url && (
                <Box mt={1}>
                  <Button
                    style={{ minWidth: '144px' }}
                    fullWidth
                    role="button"
                    color="secondary"
                    component="a"
                    href={url}
                    onClick={() => handleCtaBtn('See profile')}
                    size="small"
                    variant="contained">
                    See profile
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        {hiredTimes > 0 && (
          <Box mt={{ xs: 0, sm: 2 }} mb={{ xs: 2, sm: 1 }} data-testid="desktop-hiredby">
            <HiredBy count={hiredTimes} />
          </Box>
        )}

        {caregiverProfile?.bio.title && isMobileOrSmaller && (
          <CaregiverBioTitle caregiver={caregiver} />
        )}

        {caregiverProfile?.bio.experienceSummary && (
          <>
            <Box mt={1}>
              <CaregiverBio caregiver={caregiver} />
            </Box>
            {isAiAssistedBio && <AssistedBioIndicator />}
          </>
        )}
        <CaregiverServices caregiver={caregiver} />
        <CaregiverSkills caregiver={caregiver} />

        {caregiverReviews.length > 0 && (
          <Grid container>
            <Grid item xs={12}>
              <Box>
                <ProfileReview reviews={caregiverReviews} />
              </Box>
            </Grid>
          </Grid>
        )}
      </Box>
      {isMobileOrSmaller && url && (
        <Box mb={{ xs: 2, sm: 3 }} pb={{ xs: 0, sm: 4 }}>
          <Button
            fullWidth
            role="button"
            color="secondary"
            component="a"
            href={url}
            onClick={() => handleCtaBtn(ctaText)}
            size="medium"
            variant="contained">
            {ctaText}
          </Button>
        </Box>
      )}
      <Box ml={-2} mr={-2}>
        <Divider />
      </Box>
    </Grid>
  );
};

export default RobustCaregiverCard;
